import React, { useState, useMemo, useEffect } from "react";
// Import UI components from lift-ui
import {
  Button, 
  Badge,
  cn,
  DataTable,
  DatePickerDateRange,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  SpinnerLoader,
  Input,
} from "lift-ui";
import { FiltersPopover } from "./SessionFiltersPopover";
import type { TableColumn } from "lift-ui";
import {
  AdminFilteredSessionsV2Response,
  AdminSessionV2DetailResponse,
  AdminSessionV2FilterPayload,
  getSessionMemoryJson,
  getSessionData,
} from "../../api";
// Icons from lucide-react
import {
  MessageSquare,
  User,
  Calendar,
  Eye,
  Loader2,
  Clock,
  Monitor,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Hash,
  Layers,
  AlertCircle,
  Search,
} from "lucide-react";
import { format } from "date-fns";
import { useChatV2 } from "../../context/ChatV2Context";
import { Markdown } from "../prompt-kit/markdown";

interface SessionsPanelProps {
  sessions: AdminFilteredSessionsV2Response | null;
  loading: boolean;
  filters: AdminSessionV2FilterPayload;
  onFiltersChange: (filters: AdminSessionV2FilterPayload) => void;
  onSessionSelect: (sessionId: string) => void;
  selectedSessionId?: string | null;
}

// Session message interface
interface SessionMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
  runId?: string;
  feedback?: {
    is_liked?: boolean;
    comment?: string;
    reasons?: string[];
  };
}

// Session data interface
interface SessionData {
  session_id: string;
  user_id?: string;
  session_name?: string;
  created_at: string;
  metadata?: Record<string, any>;
  is_active: boolean;
  messages: SessionMessage[];
}

const SessionsPanel: React.FC<SessionsPanelProps> = ({
  sessions,
  loading,
  filters,
  onFiltersChange,
  onSessionSelect,
  selectedSessionId,
}) => {
  const { userMap, brandMap } = useChatV2();

  // Sheet state for viewing session details
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<AdminSessionV2DetailResponse | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loadingSessionData, setLoadingSessionData] = useState(false);
  const [sessionDataError, setSessionDataError] = useState(false);

  // Pagination state - handled locally for client-side pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Filter state (arrays for multi-select)
  const [filterUserId, setFilterUserId] = useState<string[]>([]);
  const [filterSource, setFilterSource] = useState<string[]>([]);
  const [filterAgent, setFilterAgent] = useState<string[]>([]);
  const [filterMode, setFilterMode] = useState<string[]>([]);
  const [filterModel, setFilterModel] = useState<string[]>([]);
  const filterBrand = ""; // Brand filter removed from UI but kept for compatibility
  const [filterStartDate, setFilterStartDate] = useState<Date | undefined>(undefined);
  const [filterEndDate, setFilterEndDate] = useState<Date | undefined>(undefined);
  // Free-text search across session name and prompt
  const [searchQuery, setSearchQuery] = useState<string>("");

  // GA family — these three agent types are presented to the user as a single
  // "GA" filter pill and this set defines membership.
  const GA_AGENT_TYPES = useMemo(() => new Set(["ga", "ga_dmg", "ga_affiliate"]), []);

  // Helper function to determine if a session is from Slack
  const isSlackSession = React.useCallback((session: any): boolean => {
    let metadata = session.metadata;
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata);
      } catch (e) {
        metadata = {};
      }
    }
    
    if (!metadata || Object.keys(metadata).length === 0) return false;
    if (metadata.source?.toLowerCase() === 'slack') return true;
    if (metadata.source?.toLowerCase() === 'app' || 
        metadata.source?.toLowerCase() === 'web' ||
        metadata.source?.toLowerCase() === 'webapp') return false;
    if (metadata.from_slack === true || metadata.from_slack === 'true') return true;
    if (metadata.from_slack === false || metadata.from_slack === 'false') return false;
    if (metadata.slack_channel_id) return true;
    if (metadata.thread_ts && metadata.workspace_id) return true;
    
    const sessionName = session.session_name?.toLowerCase() || '';
    if (sessionName.includes('[slack]') || sessionName.startsWith('slack:')) return true;
    
    return false;
  }, []);

  // Helper: parse session metadata safely
  const parseMetadata = React.useCallback((session: any): Record<string, any> => {
    let metadata = session.metadata;
    if (typeof metadata === 'string') {
      try { metadata = JSON.parse(metadata); } catch { metadata = {}; }
    }
    return metadata || {};
  }, []);

  // Helper: get agent label for display.  For row badges we keep the
  // sub-type so analysts can still see which GA variant a session used.
  const getAgentLabel = React.useCallback((agent: string): string => {
    const labels: Record<string, string> = {
      default: 'Default',
      ga: 'GA',
      ga_dmg: 'GA DMG',
      ga_affiliate: 'GA Affiliate',
      pacing: 'Pacing',
      similarweb: 'SimilarWeb',
      shopify: 'Shopify',
    };
    return labels[agent] || agent;
  }, []);

  // Helper: derive the mode of a session from its metadata
  const getSessionMode = React.useCallback((session: any): string => {
    const meta = parseMetadata(session);
    if (meta.vanilla_mode === true || meta.vanilla_mode === 'true') return 'vanilla';
    if (meta.agencyMode === true || meta.agencyMode === 'true') return 'agency';
    if (meta.workflow_test === true || meta.workflow_test === 'true' || meta.workflow_id) return 'workflow';
    return 'standard';
  }, [parseMetadata]);

  // Helper: get mode label for display
  const getModeLabel = React.useCallback((mode: string): string => {
    const labels: Record<string, string> = {
      standard: 'Standard',
      vanilla: 'OpenLLM',
      agency: 'Agency',
      workflow: 'Workflow',
    };
    return labels[mode] || mode;
  }, []);

  // Helper: get model label for display.  Values mirror what the chatbot
  // app actually persists in metadata.model (see Header.tsx baseModelOptions
  // / vanillaModelOptions in Lift-AI-MFE):
  //   deep_analysis  -> Deep Analysis        (Claude Opus)
  //   claude         -> Advanced Report Gen  (Claude Sonnet)
  //   gemini_pro     -> Extended Analysis    (Gemini 3.1 Pro)
  //   gemini_flash   -> Quick Analysis       (Gemini 3 Flash)
  // Older keys ("extended_analysis", "advanced_report_generation") are kept
  // as aliases so historical sessions still render with friendly names.
  const getModelLabel = React.useCallback((model: string): string => {
    const labels: Record<string, string> = {
      deep_analysis: 'Deep Analysis',
      claude: 'Advanced Report Generation',
      gemini_pro: 'Extended Analysis',
      gemini_flash: 'Quick Analysis',
      // Vanilla / OpenLLM mode model values (sent as raw aliases by the app).
      gemini: 'Gemini 3 Pro Preview',
      // Backwards-compat aliases for any historical metadata.
      extended_analysis: 'Extended Analysis',
      advanced_report_generation: 'Advanced Report Generation',
    };
    return labels[model] || model;
  }, []);

  // Helper: extract brand names from metadata brand_map
  const getBrandNames = React.useCallback((session: any): string[] => {
    const meta = parseMetadata(session);
    const bmap = meta.brand_map;
    if (!bmap || typeof bmap !== 'object') return [];
    return Object.entries(bmap).map(([id, name]) => {
      // Use the name from session metadata, fallback to global brandMap, fallback to id
      if (name && typeof name === 'string') return name;
      if (brandMap?.[id]) return brandMap[id];
      return `Brand ${id}`;
    });
  }, [parseMetadata, brandMap]);

  // Get unique users from sessions
  const uniqueUsers = useMemo(() => {
    if (!sessions?.data || sessions.data.length === 0) return [];
    const userIds = new Set<string>();
    sessions.data.forEach((session: any) => {
      if (session.user_id) {
        userIds.add(session.user_id);
      }
    });
    return Array.from(userIds).sort((a, b) => {
      const nameA = userMap?.[a] || a;
      const nameB = userMap?.[b] || b;
      return nameA.localeCompare(nameB);
    });
  }, [sessions?.data, userMap]);

  // Get unique agent types from sessions.  Any GA variant
  // (ga / ga_dmg / ga_affiliate) is collapsed into a single 'ga'
  // option in the filter pill so analysts get one toggle instead of
  // three for the same logical agent.
  const uniqueAgents = useMemo(() => {
    if (!sessions?.data || sessions.data.length === 0) return [];
    const agents = new Set<string>();
    sessions.data.forEach((session: any) => {
      const meta = parseMetadata(session);
      const rawAgent = meta.agent || 'default';
      agents.add(GA_AGENT_TYPES.has(rawAgent) ? 'ga' : rawAgent);
    });
    return Array.from(agents).sort();
  }, [sessions?.data, parseMetadata, GA_AGENT_TYPES]);

  // Get unique modes from sessions — only show Agency, Standard, OpenLLM (vanilla)
  const uniqueModes = useMemo(() => {
    const allowedModes = ['agency', 'standard', 'vanilla'];
    if (!sessions?.data || sessions.data.length === 0) return allowedModes;
    const modes = new Set<string>();
    sessions.data.forEach((session: any) => {
      const mode = getSessionMode(session);
      if (allowedModes.includes(mode)) modes.add(mode);
    });
    // Keep stable order: agency, standard, vanilla
    return allowedModes.filter((m) => modes.has(m));
  }, [sessions?.data, getSessionMode]);

  // Client-side sorted sessions - filter out workflow tests, apply filters, and sort by latest activity
  const sortedSessions = useMemo(() => {
    if (!sessions?.data || sessions.data.length === 0) return [];
    
    // ALWAYS filter out workflow test sessions - this filter must never be removed
    // This ensures workflow sessions are only shown in the "Workflow Runs" tab
    let filteredSessions = sessions.data.filter((session: any) => {
      let metadata = session.metadata;
      if (typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          metadata = {};
        }
      }
      
      // Check multiple ways workflow sessions might be identified
      const sessionName = (session.session_name || '').toLowerCase();
      const isWorkflowSession = 
        // Check metadata flags
        metadata?.workflow_test === true || 
        metadata?.workflow_test === 'true' ||
        metadata?.workflow_test === 1 ||
        metadata?.is_workflow === true ||
        metadata?.is_workflow === 'true' ||
        metadata?.workflow_id !== undefined ||
        // Check for workflow-related metadata keys
        metadata?.workflow_name !== undefined ||
        metadata?.workflow_schedule !== undefined ||
        metadata?.workflow_agent !== undefined ||
        // Check session name patterns (common workflow naming patterns)
        sessionName.includes('workflow') ||
        (sessionName.includes('daily') && sessionName.includes('performance')) ||
        (sessionName.includes('scheduled') && sessionName.includes('report')) ||
        sessionName.includes('automated') ||
        sessionName.includes('auto-report') ||
        // Check if session name matches common workflow patterns
        /^(daily|weekly|monthly)\s+(performance|report|summary|update)/i.test(session.session_name || '');
      
      return !isWorkflowSession;
    });

    // Apply user filter
    if (filterUserId.length > 0) {
      filteredSessions = filteredSessions.filter((session: any) => filterUserId.includes(session.user_id));
    }
      
      // Apply source filter
    if (filterSource.length > 0) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const source = isSlackSession(session) ? "slack" : "app";
        return filterSource.includes(source);
      });
    }

    // Apply agent filter.  Selecting "GA" matches any GA variant
    // (ga / ga_dmg / ga_affiliate); other agents match exactly.
    if (filterAgent.length > 0) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const meta = parseMetadata(session);
        const agent = meta.agent || 'default';
        const normalised = GA_AGENT_TYPES.has(agent) ? 'ga' : agent;
        return filterAgent.includes(normalised);
      });
    }

    // Apply mode filter
    if (filterMode.length > 0) {
      filteredSessions = filteredSessions.filter((session: any) => {
        return filterMode.includes(getSessionMode(session));
      });
    }

    // Apply brand filter
    if (filterBrand) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const meta = parseMetadata(session);
        const bmap = meta.brand_map;
        if (!bmap || typeof bmap !== 'object') return false;
        return Object.keys(bmap).includes(filterBrand);
      });
    }

    // Apply model filter
    if (filterModel.length > 0) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const meta = parseMetadata(session);
        const model = meta.model || 'gemini_pro';
        return filterModel.includes(model);
      });
    }

    // Apply date range filter
    if (filterStartDate) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const sessionDate = new Date(session.created_at || session.last_updated || session.updated_at);
        return sessionDate >= filterStartDate;
      });
    }

    if (filterEndDate) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const sessionDate = new Date(session.created_at || session.last_updated || session.updated_at);
        // Set end date to end of day
        const endOfDay = new Date(filterEndDate);
        endOfDay.setHours(23, 59, 59, 999);
        return sessionDate <= endOfDay;
      });
    }

    // Apply free-text search across session name + id (case-insensitive,
    // whitespace-trimmed).  Runs last so it composes with all other filters.
    const trimmedSearch = searchQuery.trim().toLowerCase();
    if (trimmedSearch) {
      filteredSessions = filteredSessions.filter((session: any) => {
        const haystack = `${session.session_name || ''} ${session.session_id || ''}`.toLowerCase();
        return haystack.includes(trimmedSearch);
      });
    }

    // Sort by latest activity (descending)
    const sorted = [...filteredSessions].sort((a: any, b: any) => {
        const aDate = a.last_updated || a.updated_at || a.created_at;
        const bDate = b.last_updated || b.updated_at || b.created_at;
      const dateA = new Date(aDate);
      const dateB = new Date(bDate);
      
      const timeA = isNaN(dateA.getTime()) ? 0 : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? 0 : dateB.getTime();
      
      return timeB - timeA; // Newest first
    });
    
    return sorted;
  }, [sessions?.data, filterUserId, filterSource, filterAgent, filterMode, filterModel, filterStartDate, filterEndDate, searchQuery, isSlackSession, parseMetadata, getSessionMode, GA_AGENT_TYPES]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedSessions.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedSessions, page, rowsPerPage]);

  const totalPages = Math.ceil(sortedSessions.length / rowsPerPage);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0); // Reset to first page when rows per page changes
  };

  // Reset to first page when filters change
  useEffect(() => {
    setPage(0);
  }, [filterUserId, filterSource, filterAgent, filterMode, filterModel, filterStartDate, filterEndDate, searchQuery]);

  // Selecting OpenLLM (vanilla) mode disables agents — the OpenLLM
  // experience doesn't go through any agent stack.  When the user
  // narrows mode to vanilla-only we proactively clear any agent
  // selection so the dropdown can't show stale tags.
  const isVanillaOnly = filterMode.length > 0 && filterMode.every((m) => m === 'vanilla');
  useEffect(() => {
    if (isVanillaOnly && filterAgent.length > 0) {
      setFilterAgent([]);
    }
  }, [isVanillaOnly, filterAgent.length]);

  // Handle viewing session details in sheet
  const handleViewSession = async (session: AdminSessionV2DetailResponse) => {
    setSelectedSession(session);
    setSheetOpen(true);
    setLoadingSessionData(true);
    setSessionDataError(false);
    setSessionData(null);
    
    try {
      // Get session memory data
      const memoryData = await getSessionMemoryJson(session.session_id);
      const basicData = await getSessionData(session.session_id);

      // Process messages from memory data
      const messages: SessionMessage[] = [];

      if (memoryData?.runs && Array.isArray(memoryData.runs)) {
        memoryData.runs.forEach((run: any, index: number) => {
          if (run.message && run.message.content) {
            messages.push({
              role: 'user',
              content: run.message.content,
              timestamp: run.message.timestamp || run.created_at,
              runId: run.run_id || `user-${index}`,
            });
          }

          if (run.response && run.response.content) {
            messages.push({
              role: 'assistant',
              content: run.response.content,
              timestamp: run.response.timestamp || run.updated_at,
              runId: run.run_id || `assistant-${index}`,
              feedback: run.response.feedback,
            });
          }
        });
      }

      // Fallback to messages array
      if (messages.length === 0 && memoryData?.messages && Array.isArray(memoryData.messages)) {
        memoryData.messages.forEach((msg: any, index: number) => {
          if (msg.role && (msg.role === 'user' || msg.role === 'assistant') && msg.content) {
            messages.push({
              role: msg.role,
              content: msg.content,
              timestamp: msg.timestamp || msg.created_at,
              runId: msg.run_id || `${msg.role}-${index}`,
              feedback: msg.feedback,
            });
          }
        });
      }

      setSessionData({
        session_id: session.session_id,
        user_id: basicData?.user_id || memoryData?.user_id || 'Unknown',
        session_name: basicData?.session_name || memoryData?.session_name || session.session_id,
        created_at: basicData?.created_at || memoryData?.created_at || new Date().toISOString(),
        metadata: basicData?.metadata || memoryData?.metadata || {},
        is_active: basicData?.is_active !== false,
        messages,
      });
    } catch (error) {
      console.error('Error loading session data:', error);
      setSessionDataError(true);
    } finally {
      setLoadingSessionData(false);
    }
  };

  // Get truncated session name (first few lines of the question)
  const getSessionPreview = (session: AdminSessionV2DetailResponse) => {
    const name = session.session_name || session.session_id;
    if (name.length > 60) {
      return name.substring(0, 60) + '...';
    }
    return name;
  };

  // Define columns for DataTable
  const columns: TableColumn<any>[] = useMemo(() => [
    {
      key: "session_name",
      header: "Session Name",
      accessor: (row: any) => row.session_name || row.session_id,
      width: "35%",
      render: (_v: any, row: any) => (
        <div className="font-medium text-[#242424]">
          <div className="truncate" title={row.session_name || row.session_id}>
            {getSessionPreview(row)}
          </div>
          {row.chat_length ? (
            <div className="flex items-center gap-1 text-xs text-[#585755] mt-0.5">
              <MessageSquare className="h-3 w-3" />
              {/* row.chat_length is the number of agent-memory runs;
                  multiply by 2 to express it as user + assistant messages. */}
              <span>{row.chat_length * 2} messages</span>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: "user",
      header: "User",
      accessor: (row: any) => userMap?.[row.user_id] || row.user_id || "Anonymous",
      width: "15%",
      render: (_v: any, row: any) => (
        <div className="flex items-center gap-1.5">
          <User className="h-3.5 w-3.5 text-[#585755] flex-shrink-0" />
          <span className="text-sm text-[#242424] truncate">
            {userMap?.[row.user_id] || row.user_id || "Anonymous"}
          </span>
        </div>
      ),
    },
    {
      key: "agent",
      header: "Agent",
      accessor: (row: any) => {
        const meta = parseMetadata(row);
        return meta.agent || 'default';
      },
      width: "10%",
      render: (_v: any, row: any) => {
        const meta = parseMetadata(row);
        const agent = meta.agent || 'default';
        const colorMap: Record<string, string> = {
          default: 'bg-blue-50 text-blue-700 border-blue-200',
          ga_dmg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
          ga_affiliate: 'bg-teal-50 text-teal-700 border-teal-200',
          pacing: 'bg-amber-50 text-amber-700 border-amber-200',
          similarweb: 'bg-purple-50 text-purple-700 border-purple-200',
        };
        return (
          <Badge variant="outline" className={cn("text-[11px] font-medium whitespace-nowrap", colorMap[agent] || 'bg-gray-50 text-gray-700 border-gray-200')}>
            {getAgentLabel(agent)}
          </Badge>
        );
      },
    },
    {
      key: "mode",
      header: "Mode",
      accessor: (row: any) => getSessionMode(row),
      width: "10%",
      render: (_v: any, row: any) => {
        const mode = getSessionMode(row);
        const colorMap: Record<string, string> = {
          standard: 'bg-slate-50 text-slate-600 border-slate-200',
          vanilla: 'bg-orange-50 text-orange-700 border-orange-200',
          agency: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        };
        return (
          <Badge variant="outline" className={cn("text-[11px] font-medium whitespace-nowrap", colorMap[mode] || 'bg-gray-50 text-gray-700 border-gray-200')}>
            {getModeLabel(mode)}
          </Badge>
        );
      },
    },
    {
      key: "date",
      header: "Date",
      accessor: (row: any) => row.created_at,
      width: "15%",
      render: (_v: any, row: any) => {
        const createdDate = new Date(row.created_at);
        const updatedDate = row.last_updated ? new Date(row.last_updated) : null;
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-1 text-xs text-[#242424]">
              <Calendar className="h-3 w-3 text-[#585755] flex-shrink-0" />
              <span>{format(createdDate, "MMM dd, yyyy")}</span>
            </div>
            {updatedDate && (
              <div className="flex items-center gap-1 text-[11px] text-[#585755]">
                <Clock className="h-2.5 w-2.5 flex-shrink-0" />
                <span>Updated {format(updatedDate, "HH:mm")}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: "source",
      header: "Source",
      accessor: (row: any) => isSlackSession(row) ? "Slack" : "App",
      width: "7%",
      render: (_v: any, row: any) => (
        <span className="text-[11px] font-medium text-[#57534E] px-2 py-1 bg-[#F5F5F4] border border-[#E5E5E4] rounded">
          {isSlackSession(row) ? "Slack" : "App"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "View",
      accessor: (row: any) => row.session_id,
      width: "4%",
      align: "center" as const,
      render: (_v: any, row: any) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 hover:bg-[#E5E5E4] text-[#57534E]"
          onClick={(e) => {
            e.stopPropagation();
            handleViewSession(row);
          }}
          title="View Session"
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ], [userMap, isSlackSession, parseMetadata, getAgentLabel, getSessionMode, getModeLabel]);

  return (
    <div className="flex-1 min-h-0 overflow-hidden flex flex-col bg-white px-8 pb-6">
      {/* Filters toolbar
       *
       * Layout: [Search] [Filters popover (User/Source/Agent/Mode/Model)]
       *           ─── flex grow ───
       *         [Date range picker]
       *
       * The unified popover keeps the row compact and avoids the
       * "5 dropdowns wrapping awkwardly" problem when the panel is narrow.
       */}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3 flex-shrink-0">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="space-y-1.5 w-[220px]">
            <Label className="text-sm font-medium">Search</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#585755] pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search session name..."
                className="pl-8 h-[46px] bg-white border-input hover:border-[#585755]"
                aria-label="Search sessions"
              />
            </div>
          </div>
          {/* Unified User / Source / Agent / Mode / Model filters live in
           *  a single popover so the toolbar stays compact. Search and
           *  the date range stay inline because they're high-frequency
           *  controls. The "User" section is searchable because the user
           *  list is high-cardinality. */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium opacity-0 select-none">
              Filters
            </Label>
            <FiltersPopover
              sections={[
                {
                  key: "user",
                  label: "User",
                  searchable: true,
                  searchPlaceholder: "Search users…",
                  options: uniqueUsers.map((userId) => ({
                    label: userMap?.[userId] || userId || "Anonymous",
                    value: userId,
                  })),
                  value: filterUserId,
                  onChange: setFilterUserId,
                },
                {
                  key: "source",
                  label: "Source",
                  options: [
                    { label: "Slack", value: "slack" },
                    { label: "App", value: "app" },
                  ],
                  value: filterSource,
                  onChange: setFilterSource,
                },
                {
                  key: "agent",
                  label: "Agent",
                  options: uniqueAgents.map((agent) => ({
                    label: getAgentLabel(agent),
                    value: agent,
                  })),
                  value: filterAgent,
                  onChange: setFilterAgent,
                  disabled: isVanillaOnly,
                  disabledHint: "OpenLLM mode does not use agents",
                },
                {
                  key: "mode",
                  label: "Mode",
                  options: uniqueModes.map((mode) => ({
                    label: getModeLabel(mode),
                    value: mode,
                  })),
                  value: filterMode,
                  onChange: setFilterMode,
                },
                {
                  key: "model",
                  label: "Model",
                  options: [
                    { label: "Deep Analysis", value: "deep_analysis" },
                    {
                      label: "Advanced Report Generation",
                      value: "claude",
                    },
                    { label: "Extended Analysis", value: "gemini_pro" },
                    { label: "Quick Analysis", value: "gemini_flash" },
                  ],
                  value: filterModel,
                  onChange: setFilterModel,
                },
              ]}
            />
          </div>
        </div>
        <div className="min-w-[280px]">
          <DatePickerDateRange
            initialStartDate={filterStartDate}
            initialEndDate={filterEndDate}
            onApply={(start, end) => {
              setFilterStartDate(start);
              setFilterEndDate(end);
            }}
            fiscal={false}
            yearType="calendar"
          />
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <SpinnerLoader className="h-8 w-8" />
            <span className="text-sm text-muted-foreground">Loading sessions...</span>
          </div>
        </div>
      )}
      
      {/* DataTable */}
      {!loading && (
        <DataTable
          className="flex-1 min-h-0"
          maxHeight="calc(100vh - 270px)"
          columns={columns}
          data={paginatedData}
          loading={false}
          emptyMessage="No sessions found."
          stickyHeader
          stickyFooter
          showActions={false}
          onRowClick={(row) => handleViewSession(row)}
          pagination={{
            currentPage: page + 1,
            totalPages: Math.max(1, totalPages),
            pageSize: rowsPerPage,
            totalItems: sortedSessions.length,
            onPageChange: (p) => handlePageChange(p - 1),
            onPageSizeChange: (size) => handleRowsPerPageChange(String(size)),
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      )}

      {/* Session Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent 
          side="right" 
          className="w-[800px] sm:w-[900px] sm:max-w-4xl overflow-y-auto bg-white"
        >
          <SheetHeader className="border-b border-[#E5E1DF] pb-4">
            <SheetTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Session History
            </SheetTitle>
            {selectedSession && (
              <SheetDescription className="mt-3">
                {(() => {
                  // Parse metadata once
                  const metadata = sessionData?.metadata || (() => {
                    let meta = selectedSession.metadata;
                    if (typeof meta === 'string') {
                      try { meta = JSON.parse(meta); } catch { meta = {}; }
                    }
                    return meta || {};
                  })();
                  
                  const agent = metadata.agent || 'default';
                  const model = metadata.model || 'gemini_pro';
                  const mode = (() => {
                    if (metadata.vanilla_mode === true || metadata.vanilla_mode === 'true') return 'vanilla';
                    if (metadata.agencyMode === true || metadata.agencyMode === 'true') return 'agency';
                    return 'standard';
                  })();
                  const bmap = metadata.brand_map;
                  const brandNames: string[] = [];
                  if (bmap && typeof bmap === 'object') {
                    Object.entries(bmap).forEach(([id, name]) => {
                      if (name && typeof name === 'string') brandNames.push(name);
                      else if (brandMap?.[id]) brandNames.push(brandMap[id]);
                      else brandNames.push(`Brand ${id}`);
                    });
                  }

                  const agentColorMap: Record<string, string> = {
                    default: 'bg-blue-50 text-blue-700 border-blue-200',
                    ga_dmg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    ga_affiliate: 'bg-teal-50 text-teal-700 border-teal-200',
                    pacing: 'bg-amber-50 text-amber-700 border-amber-200',
                    similarweb: 'bg-purple-50 text-purple-700 border-purple-200',
                  };
                  const modeColorMap: Record<string, string> = {
                    standard: 'bg-slate-50 text-slate-600 border-slate-200',
                    vanilla: 'bg-orange-50 text-orange-700 border-orange-200',
                    agency: 'bg-indigo-50 text-indigo-700 border-indigo-200',
                  };
                  const modelColorMap: Record<string, string> = {
                    gemini_pro: 'bg-sky-50 text-sky-700 border-sky-200',
                    deep_analysis: 'bg-violet-50 text-violet-700 border-violet-200',
                    extended_analysis: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    advanced_report_generation: 'bg-rose-50 text-rose-700 border-rose-200',
                  };

                  return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                      {/* User */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">User</span>
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">{userMap?.[selectedSession.user_id || ''] || selectedSession.user_id || 'Anonymous'}</span>
                        </div>
                      </div>

                      {/* Created At */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Created At</span>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">{format(new Date(selectedSession.created_at), "MMM dd, yyyy 'at' HH:mm")}</span>
                        </div>
                      </div>

                      {/* Channel — falls back to a sensible source-based
                        * label when no Slack channel is associated.  App
                        * sessions live in the user's default "General"
                        * channel rather than a named one. */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Channel</span>
                        <div className="flex items-center gap-1.5">
                          <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">
                            {selectedSession.channel_name
                              || (isSlackSession(selectedSession) ? 'Slack DM' : 'General')}
                          </span>
                        </div>
                      </div>

                      {/* Messages — chat_length on the row counts agent
                        * memory `runs` (one run = user msg + assistant
                        * reply).  Once we've actually loaded the session
                        * we use the real flat message count so a 1-run
                        * exchange shows as 2 messages, not 1. */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Messages</span>
                        <div className="flex items-center gap-1.5">
                          <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-sm text-foreground">
                            {sessionData?.messages?.length
                              ?? (selectedSession.chat_length ? selectedSession.chat_length * 2 : 0)}
                          </span>
                        </div>
                      </div>

                      {/* Agent */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Agent</span>
                        <div>
                          <Badge variant="outline" className={cn("text-xs font-medium", agentColorMap[agent] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                            {getAgentLabel(agent)}
                          </Badge>
                        </div>
                      </div>

                      {/* Mode */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Mode</span>
                        <div>
                          <Badge variant="outline" className={cn("text-xs font-medium", modeColorMap[mode] || 'bg-gray-50 text-gray-700 border-gray-200')}>
                            {getModeLabel(mode)}
                          </Badge>
                        </div>
                      </div>

                      {/* Model */}
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Model</span>
                        <div>
                          <Badge variant="outline" className={cn("text-xs font-medium", modelColorMap[model] || 'bg-gray-50 text-gray-600 border-gray-200')}>
                            {getModelLabel(model)}
                          </Badge>
                        </div>
                      </div>

                      {/* Brand */}
                      {brandNames.length > 0 && (
                        <div className="space-y-0.5">
                          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Brand</span>
                          <div className="flex flex-wrap gap-1">
                            {brandNames.map((name, i) => (
                              <Badge key={i} variant="outline" className="text-xs font-medium bg-cyan-50 text-cyan-700 border-cyan-200">
                                {name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </SheetDescription>
            )}
          </SheetHeader>

          <div className="mt-4">
            {loadingSessionData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2 text-sm">Loading session...</span>
              </div>
            ) : sessionData ? (
              <div className="space-y-4">
                  {sessionData.messages.length > 0 ? (
                    sessionData.messages.map((message, index) => (
                      <div
                        key={index}
                  className={cn(
                          "flex",
                          message.role === "user" ? "justify-end" : "justify-start"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-[85%] rounded-lg p-3",
                            message.role === "user"
                              ? "bg-[#F4FFC6] border border-[#16306D]/20"
                              : "bg-muted/50 border"
                          )}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            {message.role === "user" ? (
                              <User className="h-4 w-4" />
                            ) : (
                              <MessageSquare className="h-4 w-4" />
                            )}
                            <span className="text-xs font-medium capitalize">
                              {message.role}
                            </span>
                            {message.timestamp && (
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(message.timestamp), "HH:mm")}
                              </span>
                            )}
                          </div>
                          <div className="text-sm prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:my-2">
                            <Markdown>{message.content}</Markdown>
                        </div>
                        
                          {/* Feedback indicators for assistant messages */}
                          {message.role === 'assistant' && message.feedback && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#E5E1DF]">
                              {message.feedback.is_liked === true && (
                                <div className="flex items-center gap-1 text-green-600">
                                  <ThumbsUp className="h-3 w-3" />
                                  <span className="text-xs">Liked</span>
                          </div>
                        )}
                              {message.feedback.is_liked === false && (
                                <div className="flex items-center gap-1 text-red-600">
                                  <ThumbsDown className="h-3 w-3" />
                                  <span className="text-xs">Disliked</span>
                          </div>
                        )}
                              {message.feedback.comment && (
                                <span className="text-xs text-[#585755] italic">
                                  "{message.feedback.comment}"
                                </span>
                              )}
                            </div>
                          )}
                      </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No messages found in this session</p>
                    </div>
                  )}
            </div>
          ) : sessionDataError && selectedSession ? (
              <div className="space-y-4">
                {/* User prompt bubble */}
                <div className="flex justify-end">
                  <div className="max-w-[85%] rounded-lg p-3 bg-[#F4FFC6] border border-[#16306D]/20">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4" />
                      <span className="text-xs font-medium">User</span>
                    </div>
                    <div className="text-sm">
                      {selectedSession.session_name || 'Unknown prompt'}
                    </div>
                  </div>
                </div>
                {/* Generation failed bubble */}
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-lg p-3 bg-red-50 border border-red-200">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-xs font-medium text-red-600">Generation Failed</span>
                    </div>
                    <p className="text-sm text-red-600/80">
                      The response for this session could not be generated or retrieved.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-[#585755]">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Select a session to view its history</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SessionsPanel;
